import { useState } from 'react';
import { getConversations, deleteConversation } from '../../hooks/useAI';
import type { AIConversation } from '../../hooks/useAI';

interface ConversationSidebarProps {
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  activeConversationId?: string;
  refreshKey?: number;
}

export default function ConversationSidebar({ onSelectConversation, onNewChat, activeConversationId, refreshKey }: ConversationSidebarProps) {
  const [conversations, setConversations] = useState<AIConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const result = await getConversations(1, 50);
      setConversations(result.items);
      setLoaded(true);
    } catch {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  if (!loaded) {
    loadConversations();
  }

  // Re-load when refreshKey changes
  if (refreshKey !== undefined) {
    loadConversations();
  }

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } catch {
      // ignore
    }
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <button
          onClick={onNewChat}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && conversations.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        )}

        {!loading && conversations.length === 0 && (
          <div className="p-4 text-center text-sm text-gray-500">
            No conversations yet
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {conversations.map((convo) => (
            <button
              key={convo.id}
              onClick={() => onSelectConversation(convo.id)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors group relative ${
                activeConversationId === convo.id ? 'bg-blue-50 border-r-2 border-blue-600' : ''
              }`}
            >
              <p className="text-sm font-medium text-gray-900 truncate pr-6">
                {convo.title || 'Untitled conversation'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-400">
                  {convo.provider_used === 'vsends' ? 'VSENDS' : convo.provider_used}
                </span>
                <span className="text-xs text-gray-300">·</span>
                <span className="text-xs text-gray-400">{convo.message_count} msgs</span>
              </div>
              <button
                onClick={(e) => handleDelete(e, convo.id)}
                className="absolute right-2 top-3 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
